/**
 * Finance money helpers: integer paise only. Throws QuantityError on a non-integer or unsafe value.
 * Rounding is half away from zero (same as PostgreSQL round(numeric)), so the desk and the database agree.
 * Display uses formatInr from units/quantities (not duplicated here).
 */
import { paise, QuantityError, type Paise } from '../units/quantities';

export function addPaise(...xs: Paise[]): Paise {
  return sumPaise(xs);
}

export function sumPaise(xs: readonly Paise[]): Paise {
  let total = 0;
  for (const x of xs) total = paise(total + paise(x));
  return total as Paise;
}

export function negPaise(p: Paise): Paise {
  return paise(p === 0 ? 0 : -paise(p));
}

export function mulPaise(unit: Paise, qty: number): Paise {
  if (!Number.isInteger(qty)) throw new QuantityError('NOT_INTEGER', `Quantity must be an integer, got ${qty}`);
  paise(unit);
  const r = unit * qty;
  return paise(r === 0 ? 0 : r);
}

/** Round any finite real to whole paise, halves away from zero (2.5 -> 3, -2.5 -> -3). */
export function roundHalfUpPaise(x: number): Paise {
  if (!Number.isFinite(x)) throw new QuantityError('NOT_INTEGER', `Not a finite number: ${x}`);
  const r = Math.sign(x) * Math.round(Math.abs(x));
  return paise(r === 0 ? 0 : r);
}

/** p x basisPoints / 10000 (100 bp = 1%), exact integer arithmetic, halves away from zero. */
export function percentOfPaise(p: Paise, basisPoints: number): Paise {
  paise(p);
  if (!Number.isInteger(basisPoints)) {
    throw new QuantityError('NOT_INTEGER', `Basis points must be an integer, got ${basisPoints}`);
  }
  const num = BigInt(p) * BigInt(basisPoints);
  const neg = num < 0n;
  const abs = neg ? -num : num;
  let q = abs / 10000n;
  if (abs % 10000n >= 5000n) q += 1n;
  const out = Number(neg ? -q : q);
  return paise(out === 0 ? 0 : out);
}

/** Nearest whole rupee (halves away from zero); roundOff = total - p, so total = p + roundOff. */
export function roundToRupee(p: Paise): { total: Paise; roundOff: Paise } {
  paise(p);
  const abs = Math.abs(p);
  let rupees = Math.floor(abs / 100);
  if (abs % 100 >= 50) rupees += 1;
  const total = paise(p < 0 ? (rupees === 0 ? 0 : -rupees * 100) : rupees * 100);
  return { total, roundOff: paise(total - p) };
}

/** "1234.50" for a UPI link, from integers; no float division. Negative amounts are refused. */
export function upiAmount(p: Paise): string {
  paise(p);
  if (p < 0) throw new QuantityError('NEGATIVE', 'A UPI amount must not be negative');
  return `${Math.floor(p / 100)}.${String(p % 100).padStart(2, '0')}`;
}
